import { CheckBox, CheckBoxOutlineBlank, InfoOutlined } from "@mui/icons-material";
import { Stack, Typography } from "@mui/material";
import Enumerable from "linq";
import { JSX } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { CheckListItemType } from "../../components/check-list/CheckListItemType";
import FormCheckbox from "../../components/forms/form-checkbox/FormCheckbox";
import NavigationViewListUtils from "../../components/navigation-view-list/NavigationViewListUtils";
import { UIViewSubCategory } from "../../routes/routes";
import { UICustomizationFieldType } from "./UICustomizationSettingsFieldTypes";
import UICustomizationViewSettings from "./UICustomizationViewSettings";
import { useTranslation } from "react-i18next";
import { isMobile, isTablet } from "react-device-detect";

export type UICustomizationViewCategoryItem = {
    order: number;
    categoryLabel: string | undefined;
    views: Array<CheckListItemType>;
};

export type UICustomizationViewCategorySettingsProps = {
    fieldKey: FieldKeyType;
};

type FieldKeyType =
    "customizationSettings.navigationDrawer.viewCategories.group" |
    "customizationSettings.navigationDrawer.viewCategories.device" |
    "customizationSettings.navigationDrawer.viewCategories.well" |
    "customizationSettings.navigationDrawer.viewCategories.tankBattery" |
    "customizationSettings.navigationDrawer.viewCategories.tank" |
    "customizationSettings.navigationDrawer.viewCategories.tag";

export default function UICustomizationViewCategorySettings({
    fieldKey
}: UICustomizationViewCategorySettingsProps): JSX.Element {

    const { t } = useTranslation();
    const isPhone = isMobile && !isTablet;

    const {
        watch
    } = useFormContext();

    const {
        fields,
        update,
        move
    } = useFieldArray<UICustomizationFieldType, `${FieldKeyType}.views`, "fieldKey">({
        name: `${fieldKey}.views`,
        keyName: "fieldKey"
    });

    const [watchViews] = watch([`${fieldKey}.views`]);

    const views = fields.map((field, index) => {
        return {
            ...field,
            ...watchViews[index],
            fileKey: `files.${index}` as FieldKeyType
        };
    });

    const ungrouped = watch(`${fieldKey}.ungrouped`);

    const groupedRoutes: Array<UICustomizationViewCategoryItem> = Enumerable
        .from(views)
        .groupBy(
            e => !ungrouped
                ? e.subCategory ?? UIViewSubCategory.Uncategorized
                : UIViewSubCategory.Uncategorized,
            e => ({
                key: e.fieldKey,
                id: e.viewID,
                displayText: e.name,
                checked: !e.hidden,
                active: true,
                order: e.order,
                disabled: e.customizationDisabled
            }),
            (k, e) => ({
                order: NavigationViewListUtils.getSubcategoryDefaultOrder(k),
                categoryLabel: NavigationViewListUtils.getSubcategoryLabel(k),
                views: e.toArray()
            })
        )
        .orderBy(e => e.order)
        .toArray();

    function handleItemClick(item: CheckListItemType) {
        const itemIndex = views.findIndex(f => f.viewID === item.id);
        if (itemIndex >= 0) {
            update(itemIndex, { ...views[itemIndex], hidden: !item.checked });
        }
    }

    function handleItemMove(category: UICustomizationViewCategoryItem, oldIndex: number, newIndex: number) {
        const item1 = category.views[oldIndex];
        const item2 = category.views[newIndex];

        if (item1) {
            const fieldIndex1 = views.findIndex(f => f.viewID === item1.id);
            const fieldIndex2 = views.findIndex(f => f.viewID === item2.id);
            move(fieldIndex1, fieldIndex2);
        }
    }

    return (
        <Stack
            direction="column"
            sx={{
                paddingLeft: 1,
                paddingRight: 1,
                overflow: "hidden"
            }}
        >
            <Stack
                direction="row"
                spacing={4}
                sx={{
                    paddingTop: 1,
                    paddingBottom: 1,
                    alignItems: 'center',
                }}
            >
                {!isPhone &&
                    <Stack direction="row" spacing={1} sx={{ flex: "1 1 auto", alignItems: 'center' }}>
                        <InfoOutlined color="action" />
                        <Typography sx={{ flex: "1 1 auto" }}>
                            {t("uiCustomizationSettings.infoText")}
                        </Typography>
                    </Stack>
                }

                <FormCheckbox
                    name={`${fieldKey}.ungrouped`}
                    label={t("uiCustomizationSettings.showHeaderName")}
                    sx={{ flex: "0 0 auto" }}
                    icon={<CheckBox color="secondary" />}
                    checkedIcon={<CheckBoxOutlineBlank color="primary" />}
                />
            </Stack>

            <Stack direction="column"
                sx={{
                    padding: 0,
                    overflowY: 'auto',
                }}
            >
                {groupedRoutes.map((category, index) => {
                    return (
                        <UICustomizationViewSettings
                            key={index}
                            category={category}
                            onItemClick={handleItemClick}
                            onItemMove={(oldIndex, newIndex) => handleItemMove(category, oldIndex, newIndex)}
                        />
                    );
                })}
            </Stack>
        </Stack>
    );
}